import { PageBlueprint } from '@backstage/frontend-plugin-api';
import {
  EntityCardBlueprint,
  EntityContentBlueprint,
} from '@backstage/plugin-catalog-react/alpha';
import Grid from '@material-ui/core/Grid';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import apiDocsPlugin from '@backstage/plugin-api-docs/alpha';
import {
  EntityConsumedApisCard,
  EntityProvidedApisCard,
} from '@backstage/plugin-api-docs';
import { NonEmptyRelationCard } from '../catalog/NonEmptyRelationCard';
import {
  CatalogTable,
  CatalogTableColumnsFunc,
} from '@backstage/plugin-catalog';
import {
  CatalogFilterLayout,
  EntityKindPicker,
  EntityLifecyclePicker,
  EntityListProvider,
  EntityOwnerPicker,
  EntityTagPicker,
  EntityTypePicker,
  UserListPicker,
} from '@backstage/plugin-catalog-react';
import {
  Content,
  CreateButton,
  SupportButton,
} from '@backstage/core-components';
import { HeaderPage } from '@backstage/ui';
import ExtensionIcon from '@material-ui/icons/Extension';
import {
  applyColumnLayout,
  ColumnLayoutPersistence,
  persistedTableOptions,
} from '../tableColumnLayout';
import { LifecyclePill, TypePill } from '../../components/tablePills';

// The stock API explorer page passes no tableOptions to CatalogTable and its
// page component isn't exported, so this replicates NfsApiExplorerPage (same
// header, filters and default columns) with the shared draggable/resizable
// column layout applied (see ../tableColumnLayout).
const columns: CatalogTableColumnsFunc = () =>
  applyColumnLayout([
    CatalogTable.columns.createTitleColumn({ hidden: true }),
    CatalogTable.columns.createNameColumn({ defaultKind: 'API' }),
    CatalogTable.columns.createSystemColumn(),
    CatalogTable.columns.createOwnerColumn(),
    {
      ...CatalogTable.columns.createSpecTypeColumn(),
      render: ({ entity }) => <TypePill type={entity.spec?.type} />,
    },
    {
      ...CatalogTable.columns.createSpecLifecycleColumn(),
      render: ({ entity }) => (
        <LifecyclePill lifecycle={entity.spec?.lifecycle} />
      ),
    },
    CatalogTable.columns.createMetadataDescriptionColumn(),
    CatalogTable.columns.createTagsColumn(),
  ]);

function SavrApiExplorerPage() {
  const orgName =
    useApi(configApiRef).getOptionalString('organization.name') ?? 'Backstage';
  return (
    <ColumnLayoutPersistence>
      <HeaderPage
        title={`${orgName} API Explorer`}
        customActions={
          <>
            <CreateButton title="Register Existing API" to="/catalog-import" />
            <SupportButton>All your APIs</SupportButton>
          </>
        }
      />
      <Content>
        <EntityListProvider>
          <CatalogFilterLayout>
            <CatalogFilterLayout.Filters>
              <EntityKindPicker initialFilter="api" hidden />
              <EntityTypePicker />
              <UserListPicker initialFilter="all" />
              <EntityOwnerPicker />
              <EntityLifecyclePicker />
              <EntityTagPicker />
            </CatalogFilterLayout.Filters>
            <CatalogFilterLayout.Content>
              <CatalogTable
                columns={columns}
                tableOptions={persistedTableOptions}
              />
            </CatalogFilterLayout.Content>
          </CatalogFilterLayout>
        </EntityListProvider>
      </Content>
    </ColumnLayoutPersistence>
  );
}

const apiDocsExplorerPage = PageBlueprint.make({
  params: {
    path: '/api-docs',
    routeRef: apiDocsPlugin.routes.root,
    title: 'APIs',
    icon: <ExtensionIcon fontSize="inherit" />,
    loader: async () => <SavrApiExplorerPage />,
  },
});

// Replace the stock Provided/Consumed APIs cards (same extension ids) with
// versions that render only when the entity actually has such relations -
// empty placeholder cards are noise on the Overview page.
const savrProvidedApisCard = EntityCardBlueprint.make({
  name: 'provided-apis',
  params: {
    filter: { kind: 'component' },
    loader: async () => (
      <NonEmptyRelationCard type="providesApi">
        <EntityProvidedApisCard />
      </NonEmptyRelationCard>
    ),
  },
});
const savrConsumedApisCard = EntityCardBlueprint.make({
  name: 'consumed-apis',
  params: {
    filter: { kind: 'component' },
    loader: async () => (
      <NonEmptyRelationCard type="consumesApi">
        <EntityConsumedApisCard />
      </NonEmptyRelationCard>
    ),
  },
});

// Replace the stock APIs tab (same extension id): it rendered for every
// component, showing two empty tables on services without any APIs. The
// function filter hides the tab entirely unless the entity provides or
// consumes at least one API.
const savrApisEntityContent = EntityContentBlueprint.make({
  name: 'apis',
  params: {
    path: '/apis',
    title: 'APIs',
    group: 'development',
    filter: entity =>
      entity.kind.toLocaleLowerCase('en-US') === 'component' &&
      Boolean(
        entity.relations?.some(relation =>
          ['providesapi', 'consumesapi'].includes(
            relation.type.toLocaleLowerCase('en-US'),
          ),
        ),
      ),
    loader: async () => (
      <Grid container spacing={3} alignItems="stretch">
        <Grid item xs={12}>
          <NonEmptyRelationCard type="providesApi">
            <EntityProvidedApisCard />
          </NonEmptyRelationCard>
        </Grid>
        <Grid item xs={12}>
          <NonEmptyRelationCard type="consumesApi">
            <EntityConsumedApisCard />
          </NonEmptyRelationCard>
        </Grid>
      </Grid>
    ),
  },
});

export const apiDocsPluginWithOverrides = apiDocsPlugin.withOverrides({
  extensions: [
    apiDocsExplorerPage,
    savrProvidedApisCard,
    savrConsumedApisCard,
    savrApisEntityContent,
  ],
});
